/**
 * Protocol Media Vuex Store Module
 * Handles dental protocol media with batch upload workflow
 */

import ProtocolMediaService from '@/services/ProtocolMediaService';
import { eventBus } from '@/main';

export default {
  namespaced: true,

  state: {
    // Local photo data for preview and upload
    localPhotos: {
      upper: {
        file: null,
        base64: null,
        uploaded: false,
        uploadProgress: 0,
        metadata: null,
        capturedAt: null
      },
      lower: {
        file: null,
        base64: null,
        uploaded: false,
        uploadProgress: 0,
        metadata: null,
        capturedAt: null
      }
    },

    // API response data after upload
    protocolMedia: [],

    // Upload state
    uploading: false,
    uploadError: null,
    totalUploadProgress: 0,

    // Protocol progress
    protocolProgress: {
      completed_views: [],
      remaining_views: ['upper_occlusal', 'lower_occlusal', 'frontal_view', 'right_lateral', 'left_lateral'],
      completion_percentage: 0,
      total_files: 0,
      editable_files: 0
    },

    // Current workflow context
    currentTreatmentId: null,
    currentPatient: null,
    clinicUrl: null,
    branchUrl: null
  },

  mutations: {
    // Local photo mutations
    SET_LOCAL_PHOTO(state, { type, photoData }) {
      if (state.localPhotos[type]) {
        state.localPhotos[type] = {
          ...state.localPhotos[type],
          ...photoData,
          capturedAt: photoData.file ? new Date().toISOString() : state.localPhotos[type].capturedAt
        };
      }
    },

    CLEAR_LOCAL_PHOTO(state, type) {
      if (state.localPhotos[type]) {
        state.localPhotos[type] = {
          file: null,
          base64: null,
          uploaded: false,
          uploadProgress: 0,
          metadata: null,
          capturedAt: null
        };
      }
    },

    UPDATE_UPLOAD_PROGRESS(state, { type, progress }) {
      if (state.localPhotos[type]) {
        state.localPhotos[type].uploadProgress = progress;
      }
    },

    SET_PHOTO_UPLOADED(state, { type, uploaded }) {
      if (state.localPhotos[type]) {
        state.localPhotos[type].uploaded = uploaded;
      }
    },

    // Protocol media mutations
    SET_PROTOCOL_MEDIA(state, mediaList) {
      state.protocolMedia = mediaList || [];
    },

    ADD_PROTOCOL_MEDIA(state, mediaItem) {
      const existingIndex = state.protocolMedia.findIndex(item => item.id === mediaItem.id);
      if (existingIndex >= 0) {
        state.protocolMedia[existingIndex] = mediaItem;
      } else {
        state.protocolMedia.push(mediaItem);
      }
    },

    UPDATE_PROTOCOL_MEDIA(state, { id, updates }) {
      const index = state.protocolMedia.findIndex(item => item.id === id);
      if (index >= 0) {
        state.protocolMedia[index] = { ...state.protocolMedia[index], ...updates };
      }
    },

    REMOVE_PROTOCOL_MEDIA(state, id) {
      state.protocolMedia = state.protocolMedia.filter(item => item.id !== id);
    },

    // Upload state mutations
    SET_UPLOADING(state, uploading) {
      state.uploading = uploading;
    },

    SET_UPLOAD_ERROR(state, error) {
      state.uploadError = error;
    },

    SET_TOTAL_UPLOAD_PROGRESS(state, progress) {
      state.totalUploadProgress = progress;
    },

    // Protocol progress mutations
    SET_PROTOCOL_PROGRESS(state, progress) {
      state.protocolProgress = { ...state.protocolProgress, ...progress };
    },

    // Context mutations
    SET_WORKFLOW_CONTEXT(state, context) {
      state.currentTreatmentId = context.treatmentId;
      state.currentPatient = context.patient;
      state.clinicUrl = context.clinicUrl;
      state.branchUrl = context.branchUrl;
    },

    // Reset mutations
    RESET_LOCAL_PHOTOS(state) {
      Object.keys(state.localPhotos).forEach(type => {
        state.localPhotos[type] = {
          file: null,
          base64: null,
          uploaded: false,
          uploadProgress: 0,
          metadata: null,
          capturedAt: null
        };
      });
    },

    RESET_PROTOCOL_MEDIA(state) {
      state.protocolMedia = [];
      state.protocolProgress = {
        completed_views: [],
        remaining_views: ['upper_occlusal', 'lower_occlusal', 'frontal_view', 'right_lateral', 'left_lateral'],
        completion_percentage: 0,
        total_files: 0,
        editable_files: 0
      };
    },

    RESET_ALL(state) {
      // Reset local photos
      Object.keys(state.localPhotos).forEach(type => {
        state.localPhotos[type] = {
          file: null,
          base64: null,
          uploaded: false,
          uploadProgress: 0,
          metadata: null,
          capturedAt: null
        };
      });

      // Reset protocol media
      state.protocolMedia = [];
      state.protocolProgress = {
        completed_views: [],
        remaining_views: ['upper_occlusal', 'lower_occlusal', 'frontal_view', 'right_lateral', 'left_lateral'],
        completion_percentage: 0,
        total_files: 0,
        editable_files: 0
      };

      // Reset upload state
      state.uploading = false;
      state.uploadError = null;
      state.totalUploadProgress = 0;

      // Keep context for current session
    }
  },

  actions: {
    // Initialize workflow context
    initializeWorkflow({ commit }, context) {
      commit('SET_WORKFLOW_CONTEXT', context);
    },

    // Save local photo (for preview only)
    saveLocalPhoto({ commit }, { type, file, base64 }) {
      const photoData = {
        file: file,
        base64: base64,
        uploaded: false,
        uploadProgress: 0,
        metadata: file ? {
          size: file.size,
          type: file.type,
          name: file.name,
          lastModified: file.lastModified
        } : null
      };

      commit('SET_LOCAL_PHOTO', { type, photoData });
    },

    // Clear local photo
    clearLocalPhoto({ commit }, type) {
      commit('CLEAR_LOCAL_PHOTO', type);
    },

    // Batch upload all local photos to ProtocolMedia API
    async batchUploadPhotos({ state, commit, dispatch }) {
      const { clinicUrl, branchUrl, currentPatient, currentTreatmentId } = state;

      if (!clinicUrl || !branchUrl || !currentPatient) {
        throw new Error('Workflow context not initialized');
      }

      // Get photos to upload
      const photosToUpload = Object.entries(state.localPhotos)
        .filter(([type, photo]) => photo.file && !photo.uploaded)
        .map(([type, photo]) => ({ type, ...photo }));

      if (photosToUpload.length === 0) {
        throw new Error('No photos to upload');
      }

      commit('SET_UPLOADING', true);
      commit('SET_UPLOAD_ERROR', null);
      commit('SET_TOTAL_UPLOAD_PROGRESS', 0);

      try {
        // Step 1: Create file metadata for API
        const filesMetadata = photosToUpload.map(photo => 
          ProtocolMediaService.createFileMetadata(
            photo.file,
            photo.type, // arch: upper/lower
            'occlusal'  // surface: occlusal
          )
        );

        // Step 2: Create batch presigned URLs
        const uploadRequest = ProtocolMediaService.createUploadRequest({
          filesMetadata: filesMetadata
        });

        const presignedResponse = await ProtocolMediaService.createBatchPresignedUrls(
          clinicUrl,
          branchUrl,
          currentPatient.id,
          uploadRequest
        );

        // Step 3: Upload files to S3 with progress tracking
        const uploadTasks = photosToUpload.map((photo, index) => ({
          presignedUrl: presignedResponse.upload_urls[index].url,
          file: photo.file,
          onProgress: (progress) => {
            commit('UPDATE_UPLOAD_PROGRESS', { type: photo.type, progress });
            
            // Calculate total progress
            const totalProgress = photosToUpload.reduce((sum, p) => {
              return sum + (state.localPhotos[p.type].uploadProgress || 0);
            }, 0) / photosToUpload.length;
            
            commit('SET_TOTAL_UPLOAD_PROGRESS', Math.round(totalProgress));
          }
        }));

        const uploadResults = await ProtocolMediaService.batchUploadToS3(uploadTasks);

        // Step 4: Check upload results
        const failedUploads = uploadResults.filter(result => !result.success);
        if (failedUploads.length > 0) {
          const errorMessage = `Upload failed for ${failedUploads.length} file(s)`;
          commit('SET_UPLOAD_ERROR', errorMessage);
          throw new Error(errorMessage);
        }

        // Step 5: Register the uploads. The server answers once they are
        // stored, so the refresh below finds them (it used to race the S3 hook).
        const registration = await ProtocolMediaService.completeUploads(
          clinicUrl,
          branchUrl,
          presignedResponse.upload_urls.map(u => u.uploadId)
        );
        if (registration.failed && registration.failed.length > 0) {
          const errorMessage = `บันทึกภาพไม่สำเร็จ ${registration.failed.length} ภาพ`;
          commit('SET_UPLOAD_ERROR', errorMessage);
          throw new Error(errorMessage);
        }

        // Step 6: Mark photos as uploaded and store protocol data
        photosToUpload.forEach(photo => {
          commit('SET_PHOTO_UPLOADED', { type: photo.type, uploaded: true });
        });

        // Step 7: Fetch the uploaded media data
        await dispatch('refreshProtocolMedia');

        // Step 8: Update protocol progress
        if (presignedResponse.protocol_progress) {
          commit('SET_PROTOCOL_PROGRESS', presignedResponse.protocol_progress);
        }

        // Upload completed successfully

        return {
          uploadedCount: photosToUpload.length,
          protocolProgress: state.protocolProgress
        };

      } catch (error) {
        console.error('Batch upload failed:', error);
        commit('SET_UPLOAD_ERROR', error.message);
        
        eventBus.$emit('alertToast', {
          message: `อัปโหลดภาพล้มเหลว: ${error.message}`,
          variant: 'danger'
        });
        
        throw error;
      } finally {
        commit('SET_UPLOADING', false);
      }
    },

    // Refresh protocol media list from API
    async refreshProtocolMedia({ state, commit }) {
      const { clinicUrl, branchUrl, currentPatient, currentTreatmentId } = state;

      if (!clinicUrl || !branchUrl || !currentPatient) {
        return;
      }

      try {
        const response = await ProtocolMediaService.getProtocolMediaList(
          clinicUrl,
          branchUrl,
          currentPatient.id,
          { 
            treatmentId: currentTreatmentId,
            limit: 50 // Get all for current treatment
          }
        );

        commit('SET_PROTOCOL_MEDIA', response.media_files || []);
        
        if (response.protocol_progress) {
          commit('SET_PROTOCOL_PROGRESS', response.protocol_progress);
        }

      } catch (error) {
        console.error('Failed to refresh protocol media:', error);
      }
    },

    // Update existing protocol media (within edit window)
    async updateProtocolMedia({ state, commit }, { mediaId, file, remark }) {
      const { clinicUrl, branchUrl, currentPatient } = state;

      if (!clinicUrl || !branchUrl || !currentPatient) {
        throw new Error('Workflow context not initialized');
      }

      try {
        // Create file data
        const fileData = ProtocolMediaService.createFileData(file, 'updated');

        // Get update presigned URL
        const updateResponse = await ProtocolMediaService.updatePresignedUrl(
          clinicUrl,
          branchUrl,
          currentPatient.id,
          mediaId,
          fileData
        );

        // Upload to S3
        await ProtocolMediaService.uploadToS3(updateResponse.url, file);

        // Update local state
        commit('UPDATE_PROTOCOL_MEDIA', { 
          id: mediaId, 
          updates: { 
            edit_status: updateResponse.edit_window,
            last_modified_at: new Date().toISOString()
          }
        });

        // Photo updated successfully

      } catch (error) {
        console.error('Update protocol media failed:', error);
        eventBus.$emit('alertToast', {
          message: `แก้ไขภาพล้มเหลว: ${error.message}`,
          variant: 'danger'
        });
        throw error;
      }
    },

    // Reset workflow
    resetWorkflow({ commit }) {
      commit('RESET_ALL');
    }
  },

  getters: {
    // Local photo getters
    hasLocalPhotos: state => {
      return Object.values(state.localPhotos).some(photo => photo.file);
    },

    localPhotoCount: state => {
      return Object.values(state.localPhotos).filter(photo => photo.file).length;
    },

    uploadedPhotoCount: state => {
      return Object.values(state.localPhotos).filter(photo => photo.uploaded).length;
    },

    photosReadyForUpload: state => {
      return Object.values(state.localPhotos).filter(photo => photo.file && !photo.uploaded).length;
    },

    // Photo preview URLs
    upperPhotoPreview: state => {
      return state.localPhotos.upper.base64;
    },

    lowerPhotoPreview: state => {
      return state.localPhotos.lower.base64;
    },

    // Protocol media getters
    upperPhotoMediaUrl: state => {
      const media = state.protocolMedia.find(m => 
        m.protocol_info?.view_details?.arch === 'upper'
      );
      return media ? media.mediaURL : null;
    },

    lowerPhotoMediaUrl: state => {
      const media = state.protocolMedia.find(m => 
        m.protocol_info?.view_details?.arch === 'lower'
      );
      return media ? media.mediaURL : null;
    },

    // Edit capabilities
    canEditPhotos: state => {
      return state.protocolMedia.some(media => media.edit_status?.can_edit);
    },

    editablePhotos: state => {
      return state.protocolMedia.filter(media => media.edit_status?.can_edit);
    },

    // Protocol progress
    protocolCompletionPercentage: state => {
      return state.protocolProgress.completion_percentage || 0;
    },

    isProtocolComplete: state => {
      return state.protocolProgress.completion_percentage >= 100;
    },

    // Upload status
    isUploading: state => {
      return state.uploading;
    },

    uploadProgressPercentage: state => {
      return state.totalUploadProgress;
    },

    hasUploadError: state => {
      return !!state.uploadError;
    },

    // Workflow readiness
    canProceedToNextStep: state => {
      const hasRequiredPhotos = state.localPhotos.upper.file && state.localPhotos.lower.file;
      const allUploaded = Object.values(state.localPhotos)
        .filter(photo => photo.file)
        .every(photo => photo.uploaded);
      
      return hasRequiredPhotos && allUploaded && !state.uploading;
    }
  }
};